import {
  Platform,
  Pressable,
  ScrollView,
  View,
  type PressableProps,
} from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  ToolbarGlassSurface,
  ToolbarViewButton,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import type { ReactNode } from "react";
import { AdminPanelControlsSection } from "#admin-panel-controls-section";
import { AdminPanelLevelSection } from "#admin-panel-level-section";
import { AdminPanelMoreMenu } from "#admin-panel-more-menu";
import { AdminPanelReviewQueueSection } from "#admin-panel-review-queue-section";
import {
  ADMIN_PANEL_TABS,
  type AdminPanelPlacement,
  type AdminPanelTab,
} from "#admin-panel-layout";
import { AdminPanelWalletSection } from "#admin-panel-wallet-section";

/**
 * The one app-wide Admin panel: superpowers over the signed-in account's own data. It is the body
 * of the sheet that a swipe down from the drawer button opens (see `AdminPanelGesture`): a toolbar
 * row (the More menu, glass tabs for each subview and, at the top, the drawer button) over the
 * selected subview.
 */
export function AdminPanel({
  handleHeight,
  onClose,
  onPlacementChange,
  onTabChange,
  placement,
  tab,
  trailingControl,
}: {
  /** The drag handle overlaying the sheet's free edge: below the content, or above it at the bottom. */
  handleHeight: number;
  onClose: () => void;
  onPlacementChange: (placement: AdminPanelPlacement) => void;
  onTabChange: (tab: AdminPanelTab) => void;
  placement: AdminPanelPlacement;
  tab: AdminPanelTab;
  /** Drawn in the drawer button's corner, where the sheet covers the app's own toolbar. */
  trailingControl?: ReactNode;
}) {
  const { left, right, top } = useSafeAreaInsets();
  const { height: toolbarHeight } = useToolbarGridMetrics();
  const isTop = placement === "top";
  return (
    <View className="flex-1">
      {/* The toolbar's own backdrop, so its glass tabs read as they do in the app's toolbar. */}
      <View className="shrink-0 bg-muted" style={{ paddingTop: isTop ? top : handleHeight }}>
        {/* Lines up with the app's toolbar row, so the More menu takes the page menu's corner. */}
        <View
          className="flex-row gap-2.25 px-2.25"
          style={{ height: toolbarHeight, marginLeft: left, marginRight: right }}
        >
          <View className="pt-2.25">
            <AdminPanelMoreMenu
              onClose={onClose}
              onPlacementChange={onPlacementChange}
              placement={placement}
              trigger={<AdminPanelMoreButton />}
            />
          </View>
          <ScrollView
            accessibilityRole="tablist"
            className="min-w-0 flex-1"
            contentContainerClassName="items-start gap-2.25 py-2.25"
            horizontal
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            testID="admin-panel-tabs"
          >
            {ADMIN_PANEL_TABS.map((option) => (
              <ToolbarViewButton
                key={option.id}
                label={option.label}
                onPress={() => onTabChange(option.id)}
                selected={option.id === tab}
                testID={`admin-panel-tab-${option.id}`}
              />
            ))}
          </ScrollView>
          {trailingControl === undefined ? null : (
            <View className="pt-2.25">{trailingControl}</View>
          )}
        </View>
      </View>
      <ScrollView
        automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
        className="flex-1"
        contentContainerClassName={isTop ? "gap-6 p-3 pb-14" : "gap-6 p-3"}
        // Each subview starts at its top.
        key={tab}
        keyboardDismissMode="interactive"
        keyboardShouldPersistTaps="handled"
        scrollIndicatorInsets={{ bottom: isTop ? handleHeight : 0 }}
        style={{ marginLeft: left, marginRight: right }}
        testID="admin-panel-scroll"
      >
        {tab === "level" ? (
          <AdminPanelLevelSection />
        ) : tab === "review-queue" ? (
          <AdminPanelReviewQueueSection />
        ) : tab === "wallet" ? (
          <AdminPanelWalletSection />
        ) : (
          <AdminPanelControlsSection />
        )}
      </ScrollView>
    </View>
  );
}

/** The More trigger: the toolbar's glass circle. Menu triggers merge their press handlers in. */
function AdminPanelMoreButton(props: PressableProps) {
  const { diameter } = useToolbarGridMetrics();
  return (
    <ToolbarGlassSurface style={{ height: diameter, width: diameter }}>
      <Pressable
        accessibilityLabel="Admin panel options"
        accessibilityRole="button"
        className="size-full items-center justify-center"
        hitSlop={8}
        testID="admin-panel-more"
        {...props}
      >
        <Icon className="text-foreground" name="FiMoreHorizontal" size={24} />
      </Pressable>
    </ToolbarGlassSurface>
  );
}
